import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  Avatar,
  Typography,
  CircularProgress,
  Badge,
  IconButton,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import conversationService from '../services/conversationService';
import { formatWhatsappTimestamp } from '../utils/timezone';
import { getPhoneCountryInfo } from '../utils/phoneUtils';
import { useConversationRefresh } from '../contexts/ConversationRefreshContext';

const ConversationListPanel = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { refreshKey } = useConversationRefresh();
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [totalConversations, setTotalConversations] = useState(0);
  const [offset, setOffset] = useState(0);
  const [consecutiveEmptyLoads, setConsecutiveEmptyLoads] = useState(0); // Track consecutive loads with no new items
  const [lastLoadedTimestamp, setLastLoadedTimestamp] = useState(null); // For cursor-based pagination
  const [lastLoadedSessionId, setLastLoadedSessionId] = useState(null); // For cursor-based pagination tie-breaking
  const [conversationFilter, setConversationFilter] = useState('answered'); // Filter: 'all' or 'answered'
  const searchTimeoutRef = useRef(null);
  const lastSearchRef = useRef('');
  const searchAbortControllerRef = useRef(null);
  const listRef = useRef(null);
  const isSearchActiveRef = useRef(false); // Track if search is active
  const lastConversationTimestampRef = useRef(null);
  const pendingCursorUpdateRef = useRef(null); // Pending cursor update when all conversations are duplicates
  
  // Función para normalizar texto (quitar acentos)
  const normalizeText = (text) => {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  };

  const isAnsweredConversation = (conv) =>
    (conv?.user_messages || 0) >= 1 && (conv?.bot_messages || 0) >= 1;

  // Keep ordering stable across pagination (important for cursor/infinite scroll).
  const compareConversations = (a, b) => {
    const timeDiff = new Date(b.last_message).getTime() - new Date(a.last_message).getTime();
    if (timeDiff !== 0) return timeDiff;
    const aKey = a.participant_phone || a.session_id || '';
    const bKey = b.participant_phone || b.session_id || '';
    return bKey.localeCompare(aKey);
  };



  // Function to add or update conversations - movido antes de los useEffect que lo usan
  const addNewConversations = useCallback((newConversations) => {
    if (!newConversations || newConversations.length === 0) return;
    
    // Evitar procesar las mismas conversaciones múltiples veces
    const processedIds = new Set();
    const uniqueNewConversations = newConversations.filter(conv => {
      if (processedIds.has(conv.session_id)) {
        return false;
      }
      processedIds.add(conv.session_id);
      return true;
    });
    
    if (uniqueNewConversations.length === 0) {
      console.log(`🔄 No unique conversations to process`);
      return;
    }
    
    console.log(`🔄 Processing ${uniqueNewConversations.length} unique new/updated conversations with filter: ${conversationFilter}`);
    
    // Filter conversations based on current filter
    let filteredNewConversations = uniqueNewConversations;
    if (conversationFilter === 'answered') {
      filteredNewConversations = uniqueNewConversations.filter(isAnsweredConversation);
      console.log(`  📋 After filter: ${filteredNewConversations.length} conversations (removed ${uniqueNewConversations.length - filteredNewConversations.length} unanswered)`);
    }
    
    if (filteredNewConversations.length === 0) {
      console.log(`  ⚠️ No conversations passed the filter`);
      return;
    }
    
    setConversations(prevConversations => {
      // Crear un mapa de las conversaciones existentes para actualización rápida
      // Use participant_phone as key for phone-based grouping
      const conversationMap = new Map(prevConversations.map(c => [c.participant_phone || c.session_id, c]));
      
      // Procesar las nuevas conversaciones
      let hasChanges = false;
      let addedCount = 0;
      let updatedCount = 0;
      
      filteredNewConversations.forEach(newConv => {
        // For phone-based grouping, use participant_phone as key
        const key = newConv.participant_phone || newConv.session_id;
        const existing = conversationMap.get(key);
        if (!existing) {
          // It's a completely new conversation
          conversationMap.set(key, newConv);
          hasChanges = true;
          addedCount++;
          console.log(`  ➕ New conversation added: ${key}`);
        } else if (new Date(newConv.last_message) > new Date(existing.last_message)) {
          // The conversation exists but has newer messages
          conversationMap.set(key, newConv);
          hasChanges = true;
          updatedCount++;
          console.log(`  🔄 Updated existing conversation: ${key}`);
        } else {
          console.log(`  ℹ️ Conversation ${key} already up to date`);
        }
      });
      
      console.log(`  📊 Summary: ${addedCount} added, ${updatedCount} updated`);
      
      if (!hasChanges) {
        console.log('  ℹ️ No changes detected in conversations');
        return prevConversations;
      }
      
      // Convert the map back to array and sort
      const sorted = Array.from(conversationMap.values()).sort(compareConversations);
      
      // Update the timestamp of the last conversation
      if (sorted.length > 0) {
        const lastConversation = sorted[0]; // La primera es la más reciente después del sort
        if (lastConversation.last_message) {
          lastConversationTimestampRef.current = lastConversation.last_message;
          console.log(`  📍 Updated lastConversationTimestamp to: ${lastConversation.last_message}`);
        }
      }
      
      console.log(`  ✅ Processed ${filteredNewConversations.length} conversations, changes: ${hasChanges}`);
      return sorted;
    });
    
    // Actualizar también filteredConversations si no hay búsqueda activa
    if (!isSearchActiveRef.current) {
      setFilteredConversations(prev => {
        const conversationMap = new Map(prev.map(c => [c.participant_phone || c.session_id, c]));
        
        filteredNewConversations.forEach(newConv => {
          const key = newConv.participant_phone || newConv.session_id;
          const existing = conversationMap.get(key);
          if (!existing || new Date(newConv.last_message) > new Date(existing.last_message)) {
            conversationMap.set(key, newConv);
          }
        });
        
        const sorted = Array.from(conversationMap.values()).sort(compareConversations);
        
        return sorted;
      });
    }
  }, []); // No dependencies - function uses current filter value via closure

  // Efecto para carga inicial
  useEffect(() => {
    console.log('ConversationListPanel initial load');
    // Initial load is handled by the filter effect below
  }, []); // Solo se ejecuta una vez al montar el componente

  // Efecto separado para auto-refresh
  useEffect(() => {
    // Solo configurar el intervalo si hay conversaciones cargadas
    if (conversations.length === 0) {
      console.log('Skipping conversation update interval - no conversations loaded yet');
      return;
    }

    const intervalId = `conversation-updates-${Date.now()}-${Math.random()}`;
    console.log(`🔄 Setting up conversation update interval with ID: ${intervalId}`);
    
    const interval = setInterval(async () => {
      // Verificar que no esté cargando más y que no haya búsqueda activa
      if (!isSearchActiveRef.current && !loadingMore && conversations.length > 0) {
        try {
          // If we have a timestamp from the last conversation, search from there
          // If not, search for conversations from the last 2 minutes
          const checkTime = lastConversationTimestampRef.current 
            ? new Date(lastConversationTimestampRef.current)
            : new Date(Date.now() - 120000); // 2 minutos atrás si no hay referencia
          
          console.log(`[${intervalId}] Checking for conversation updates since:`, checkTime, 'Last known:', lastConversationTimestampRef.current);
          const updates = await conversationService.checkForUpdates(checkTime);
          console.log(`[${intervalId}] Conversation updates response:`, updates);
          if (updates.has_updates && updates.new_conversations) {
            console.log(`[${intervalId}] Found new conversations:`, updates.new_conversations.length);
            // Agregar solo las conversaciones nuevas al inicio
            addNewConversations(updates.new_conversations);
          } else {
            console.log(`[${intervalId}] No new conversations found`);
          }
        } catch (error) {
          console.error(`[${intervalId}] Error checking for updates:`, error);
          // Si hay error 401, limpiar el intervalo (sesión expirada)
          if (error.response?.status === 401) {
            console.log(`[${intervalId}] Session expired, cleaning up interval`);
            clearInterval(interval);
            return;
          }
        }
      }
    }, 3000); // Chequea cada 3 segundos
  
    return () => {
      console.log(`🧹 Cleaning up conversation update interval with ID: ${intervalId}`);
      clearInterval(interval);
    };
  }, [conversations.length, loadingMore, addNewConversations]); // Dependencias corregidas

  // Efecto para reinicializar conversaciones cuando se solicita refresh
  useEffect(() => {
    if (refreshKey > 0) {
      console.log('🔄 Refreshing conversations due to refreshKey change:', refreshKey);
      // Limpiar estado y recargar
      setConversations([]);
      setFilteredConversations([]);
      setOffset(0);
      setHasMore(true);
      setConsecutiveEmptyLoads(0);
      setLastLoadedTimestamp(null);
      setLastLoadedSessionId(null);
      loadConversations();
    }
  }, [refreshKey]);

  // Detectar scroll para cargar más conversaciones
  const handleScroll = useCallback((e) => {
    const element = e.target;
    const threshold = 100; // pixels desde el fondo
    
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + threshold) {
      if (!searchTerm && hasMore && !loadingMore) {
        loadConversations(true);
      }
    }
  }, [hasMore, loadingMore, searchTerm]);

  // Búsqueda en tiempo real con debounce
  useEffect(() => {
    // Cancelar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Cancelar búsqueda anterior si está en progreso
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
      searchAbortControllerRef.current = null;
    }

    // Hacer trim del término de búsqueda
    const trimmedSearchTerm = searchTerm.trim();

    if (trimmedSearchTerm === '') {
      // When not searching, show all conversations (filtering is done in backend)
      setFilteredConversations(conversations);
      setSearching(false);
      lastSearchRef.current = '';
      isSearchActiveRef.current = false; // Mark search as inactive
      setConsecutiveEmptyLoads(0); // Reset counter when search is cleared
      return;
    }
    
    // Mark search as active
    isSearchActiveRef.current = true;

    // Mientras esperamos la búsqueda del backend, podemos mostrar un indicador
    // pero no hacer búsqueda local ya que el backend es más completo

    // Búsqueda en el backend después de 500ms
    setSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      // Verificar si ya buscamos este término
      if (trimmedSearchTerm === lastSearchRef.current) {
        setSearching(false);
        return;
      }
      
      // Crear nuevo AbortController para esta búsqueda
      searchAbortControllerRef.current = new AbortController();
      lastSearchRef.current = trimmedSearchTerm; // Mark BEFORE searching to avoid duplicates
      await performSearch(trimmedSearchTerm, searchAbortControllerRef.current.signal);
      setSearching(false);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
      }
    };
  }, [searchTerm, conversationFilter]); // Depender de searchTerm y conversationFilter

  // Effect to handle conversationFilter changes - reset and reload
  useEffect(() => {
    // Reset pagination state
    setConversations([]);
    setFilteredConversations([]);
    setOffset(0);
    setHasMore(true);
    setConsecutiveEmptyLoads(0);
    setLastLoadedTimestamp(null);
    setLastLoadedSessionId(null);
    setTotalConversations(0); // Reset total to prevent showing stale count
    lastConversationTimestampRef.current = null;
    pendingCursorUpdateRef.current = null;
    
    // Reload conversations with new filter
    console.log(`🔄 Filter changed to: ${conversationFilter}, reloading conversations...`);
    loadConversations(false);
  }, [conversationFilter]); // Only trigger when conversationFilter changes
  
  // Effect to update filtered conversations when conversations or search term changes
  useEffect(() => {
    if (searchTerm.trim() === '') {
      // Filtering is done in backend, just set conversations directly
      setFilteredConversations(conversations);
    }
  }, [conversations, searchTerm]);

  const loadConversations = useCallback(async (loadMore = false) => {
    console.log(`🔄 loadConversations called - loadMore: ${loadMore}, hasMore: ${hasMore}, loadingMore: ${loadingMore}`);
    console.log(`  Current state - offset: ${offset}, conversationFilter: ${conversationFilter}, conversations.length: ${conversations.length}`);
    
    if (loadMore && (!hasMore || loadingMore)) {
      console.log(`  ⚠️ Skipping load - hasMore: ${hasMore}, loadingMore: ${loadingMore}`);
      return;
    }
    
    try {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      // Determine pagination strategy based on filter
      const params = {
        limit: 50,
        hideUnanswered: conversationFilter === 'answered'  // Send filter to backend
      };
      
      // When filter is active OR we have cursor info, use cursor-based pagination
      if (loadMore && (conversationFilter === 'answered' || lastLoadedTimestamp)) {
        // Use cursor-based pagination for filtered results
        if (lastLoadedTimestamp) {
          params.beforeTimestamp = lastLoadedTimestamp;
          if (lastLoadedSessionId) {
            params.beforePhone = lastLoadedSessionId; // Use phone for cursor in grouped mode
          }
          console.log(`  📍 Using cursor-based pagination - beforeTimestamp: ${lastLoadedTimestamp}, beforePhone: ${lastLoadedSessionId}`);
        } else {
          // First page with filter - no cursor needed
          console.log(`  📍 First page with filter - no cursor`);
        }
        params.offset = 0; // Always 0 with cursor-based
      } else if (conversationFilter === 'all' && loadMore) {
        // Only use offset for 'all' filter
        const currentOffset = offset;
        params.offset = currentOffset;
        console.log(`  📍 Using offset-based pagination - offset: ${currentOffset}`);
      } else {
        // Initial load
        params.offset = 0;
        console.log(`  📍 Initial load - offset: 0`);
      }
      
      console.log('  📤 Request params:', params);
      // Use the new grouped endpoint
      const response = await conversationService.getSummaryGrouped(params);
      
      // The response now includes pagination information
      const newConversations = response.conversations || [];
      const total = response.total || 0;
      const hasMoreData = response.has_more || false;
      
      console.log(`  📥 Response - got ${newConversations.length} conversations, total: ${total}, has_more: ${hasMoreData}`);
      
      // Sort by last message
      const sorted = newConversations.sort(compareConversations);
      
      let shouldUpdateCursorFromExisting = false;
      
      if (loadMore) {
        // Agregar a las conversaciones existentes, evitando duplicados
        let uniqueCount = 0;
        pendingCursorUpdateRef.current = null; // Reset pending cursor update
        
        setConversations(prev => {
          // Crear un Set de phone numbers existentes para verificación rápida
          // Usar participant_phone como clave única en lugar de session_id
          const existingPhones = new Set(prev.map(c => c.participant_phone));
          
          // Filtrar solo las conversaciones nuevas que no existan ya
          const uniqueNewConversations = sorted.filter(conv => 
            !existingPhones.has(conv.participant_phone)
          );
          
          uniqueCount = uniqueNewConversations.length;
          
          if (uniqueNewConversations.length === 0) {
            console.log(`  ⚠️ No new unique conversations - all ${sorted.length} already exist`);
            shouldUpdateCursorFromExisting = true;
            
            // Save cursor info to update outside the callback
            if (prev.length > 0 && (conversationFilter === 'answered' || loadMore)) {
              const oldestExisting = prev[prev.length - 1];
              if (oldestExisting && oldestExisting.last_message) {
                pendingCursorUpdateRef.current = {
                  timestamp: oldestExisting.last_message,
                  sessionId: oldestExisting.participant_phone // Use phone instead of session_id
                };
              }
            }
            
            return prev;
          }
          
          console.log(`  ✅ Adding ${uniqueNewConversations.length} new unique conversations out of ${sorted.length} received`);
          console.log(`  📊 Total conversations after merge: ${prev.length} + ${uniqueNewConversations.length} = ${prev.length + uniqueNewConversations.length}`);
          
          // Combinar y reordenar
          const combined = [...prev, ...uniqueNewConversations];
          // Re-sort to ensure correct order
          combined.sort(compareConversations);
          return combined;
        });
        
        // Apply pending cursor update after state update
        setTimeout(() => {
          if (pendingCursorUpdateRef.current) {
            setLastLoadedTimestamp(pendingCursorUpdateRef.current.timestamp);
            setLastLoadedSessionId(pendingCursorUpdateRef.current.sessionId);
            console.log(`  📍 No new conversations - cursor moved to continue after: timestamp: ${pendingCursorUpdateRef.current.timestamp}, session_id: ${pendingCursorUpdateRef.current.sessionId}`);
            pendingCursorUpdateRef.current = null;
          }
        }, 0);
        
        // Solo actualizar filteredConversations si NO hay búsqueda activa
        if (!isSearchActiveRef.current) {
          setFilteredConversations(prev => {
            const existingPhones = new Set(prev.map(c => c.participant_phone));
            const uniqueNewConversations = sorted.filter(conv => 
              !existingPhones.has(conv.participant_phone)
            );
            if (uniqueNewConversations.length === 0) return prev;
            const combined = [...prev, ...uniqueNewConversations];
            combined.sort(compareConversations);
            // Return combined list (filtering is done in backend)
            return combined;
          });
        }
        
        // IMPORTANTE: Cuando hay filtro, NO usar offset tradicional
        // Solo incrementar offset si NO hay filtro aplicado
        if (conversationFilter === 'all') {
          setOffset(prevOffset => {
            const newOffset = prevOffset + sorted.length;
            console.log(`  📦 Updated offset to: ${newOffset} (no filter)`);
            return newOffset;
          });
        } else {
          // Con filtro, no usar offset, solo cursor-based
          console.log(`  📦 Not updating offset (filter active, using cursor-based)`);
        }
        
        // Manejar el conteo de cargas vacías consecutivas
        if (uniqueCount === 0 && sorted.length > 0) {
          // No se agregaron conversaciones únicas
          // Only count as empty load if cursor was updated and we still got duplicates
          if (pendingCursorUpdateRef.current) {
            console.log(`  ℹ️ Empty load but cursor will be updated - not counting as consecutive`);
            setConsecutiveEmptyLoads(0); // Reset counter when cursor changes
          } else {
            setConsecutiveEmptyLoads(prev => {
              const newCount = prev + 1;
              console.log(`  ⚠️ Consecutive empty loads: ${newCount}`);
              // If we've had 10 consecutive loads with no new conversations and cursor hasn't changed, assume end
              if (newCount >= 10) {
                console.log('  🎯 Multiple consecutive loads with no new conversations and no cursor changes, assuming end reached');
                setHasMore(false);
              }
              return newCount;
            });
          }
        } else if (uniqueCount > 0) {
          // Se agregaron conversaciones, resetear el contador
          console.log(`  ✅ Resetting consecutive empty loads counter`);
          setConsecutiveEmptyLoads(0);
        } else if (sorted.length === 0) {
          // Si no se recibieron conversaciones, entonces sí hemos llegado al final
          console.log('  🎯 No more conversations from backend - end reached');
          setHasMore(false);
        }
      } else {
        // Initial load - replace conversations
        console.log(`  🆕 Initial load - replacing all conversations with ${sorted.length} items`);
        setConversations(sorted);
        // Solo actualizar filteredConversations si NO hay búsqueda activa
        if (!isSearchActiveRef.current) {
          setFilteredConversations(sorted);
        }
        // Solo usar offset si no hay filtro
        if (conversationFilter === 'all') {
          setOffset(sorted.length);
          console.log(`  📦 Initial offset set to: ${sorted.length} (no filter)`);
        } else {
          setOffset(0);
          console.log(`  📦 Initial offset set to: 0 (filter active)`);
        }
      }
      
      setTotalConversations(total);
      
      // Solo marcar hasMore como false si el backend explícitamente dice que no hay más
      // o si realmente no recibimos datos
      if (sorted.length === 0) {
        setHasMore(false);
      } else {
        setHasMore(hasMoreData);
      }
      
      // Save the timestamp of the last conversation for future updates
      if (sorted.length > 0) {
        const lastConversation = sorted[0]; // La primera es la más reciente después del sort
        if (lastConversation.last_message) {
          lastConversationTimestampRef.current = lastConversation.last_message;
          console.log('Updated lastConversationTimestamp to:', lastConversation.last_message);
        }
        
              // For cursor-based pagination when we added new conversations
              if ((conversationFilter === 'answered' || loadMore) && !shouldUpdateCursorFromExisting && sorted.length > 0) {
                // Update cursor with the oldest conversation from this batch
                const oldestInBatch = sorted[sorted.length - 1];
                if (oldestInBatch.last_message) {
                  setLastLoadedTimestamp(oldestInBatch.last_message);
                  setLastLoadedSessionId(oldestInBatch.participant_phone); // Use phone for cursor
                  console.log(`  📍 Updated cursor for next page - timestamp: ${oldestInBatch.last_message}, phone: ${oldestInBatch.participant_phone}`);
                }
              }
      }
      
      setLoading(false);
      setLoadingMore(false);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setLoading(false);
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, offset, consecutiveEmptyLoads, lastLoadedTimestamp, lastLoadedSessionId, conversationFilter, conversations.length]); // Dependencies for loadConversations

  const performSearch = async (term, signal) => {
    try {
      // Detect if the term looks like a phone number
      const isPhoneSearch = /^\d+$/.test(term.replace(/[\s\-\+\(\)]/g, ''));
      
      // Buscar en el backend con el término
      const searchFilters = isPhoneSearch 
        ? { phone_number: term, limit: 2000, hide_unanswered: conversationFilter === 'answered' }  // Search specifically by phone
        : { q: term, limit: 2000, hide_unanswered: conversationFilter === 'answered' };  // Búsqueda general
      
      console.log('🔍 Search request:', {
        term,
        isPhoneSearch,
        filters: searchFilters
      });
      
      const searchResponse = await conversationService.search(searchFilters, signal);
      
      // Handle the new standardized format
      const searchResults = searchResponse.results || [];
      console.log(`Search found ${searchResponse.total || 0} total results, showing ${searchResults.length}`);
      
      if (searchResults && searchResults.length > 0) {
        // Extraer session_ids únicos de los resultados
        const sessionIds = [...new Set(searchResults.map(msg => msg.session_id))];
        console.log(`Found ${sessionIds.length} unique sessions from ${searchResults.length} messages`);
        
        // Crear conversaciones a partir de los resultados de búsqueda
        const searchConversations = sessionIds.map(sid => {
          try {
            // Get all messages from this session in the results
            const sessionMessages = searchResults.filter(m => m.session_id === sid);
            
            // Sort messages by timestamp
            sessionMessages.sort((a, b) => 
              new Date(b.message_timestamp) - new Date(a.message_timestamp)
            );
            
            const lastMessage = sessionMessages[0];
            const phone = lastMessage.user_from === '16503963355' 
              ? lastMessage.user_to 
              : lastMessage.user_from;
            
            // Find the message that best matches the search
            const matchingMessage = sessionMessages.find(m => 
              m.message && m.message.toLowerCase().includes(term.toLowerCase())
            ) || lastMessage;
            
            return {
              session_id: sid,
              participant_phone: phone,
              participant_name: null, // No name available in search results  
              message_count: sessionMessages.length, // This is just the count of found messages
              last_message: lastMessage.message_timestamp,
              last_message_text: matchingMessage.message || 'No message', // Show the matching message
              bot_messages: sessionMessages.filter(m => m.sender === 'bot').length,
              user_messages: sessionMessages.filter(m => m.sender === 'user').length,
              search_match: true, // Indicador de que es un resultado de búsqueda
              found_in_message: matchingMessage.message // Save the message where it was found
            };
          } catch (err) {
            console.error('Error procesando sesión:', sid, err);
            return null;
          }
        });
        
        // Filtrar nulls
        let validSearchConvs = searchConversations.filter(c => c !== null);
        
        // Apply conversation filter if needed (for frontend filtering of search results)
        if (conversationFilter === 'answered') {
          // Match backend: "answered" = at least one user + one bot message (not just >1 msgs)
          validSearchConvs = validSearchConvs.filter(
            (conv) => (conv.user_messages || 0) >= 1 && (conv.bot_messages || 0) >= 1
          );
        }
        
        // Sort by last message timestamp
        const sorted = validSearchConvs.sort(compareConversations);
        
        setFilteredConversations(sorted);
      } else {
        // Si no hay resultados, mostrar lista vacía
        setFilteredConversations([]);
      }
    } catch (error) {
      // Si el error es por cancelación, no hacer nada
      if (error.name === 'AbortError' || error.message === 'canceled' || error.code === 'ERR_CANCELED') {
        return;
      }
      
      console.error('Error searching conversations:', error);
      
      // En caso de error real, mostrar lista vacía
      setFilteredConversations([]);
    }
  };

  const handleConversationClick = (conversation) => {
    // Navigate using phone number instead of session ID
    const phone = encodeURIComponent(conversation.participant_phone);
    navigate(`/conversations/${phone}`);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredConversations(conversations);
  };

  const getInitials = (phone) => {
    const info = getPhoneCountryInfo(phone);
    return info.flag || '👤';
  };

  return (
    <Box
      sx={{
        width: 350,
        minWidth: 350,
        maxWidth: 350,
        height: '100vh',
        bgcolor: 'white',
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
    >
      {/* Search Bar */}
      <Box sx={{ 
        height: 56, 
        boxSizing: 'border-box', 
        display: 'flex', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        py: 1.5,
        px: 2,
      }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversation or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {searching ? (
                  <CircularProgress size={16} sx={{ color: 'text.secondary' }} />
                ) : (
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                )}
              </InputAdornment>
            ),
            endAdornment: searchTerm.trim() && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  sx={{ p: 0.5 }}
                >
                  <ClearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              bgcolor: '#f0f2f5',
              borderRadius: 2,
              '& fieldset': { border: 'none' },
              height: 36,
              pr: searchTerm ? 0.5 : 1.5,
            },
          }}
        />
      </Box>

      {/* Conversations List */}
      <Box 
        ref={listRef}
        onScroll={handleScroll}
        sx={{ flexGrow: 1, overflow: 'auto' }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={30} />
          </Box>
        ) : (searching && filteredConversations.length === 0) ? (
          // Mientras busca, mostrar indicador de búsqueda
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            p: 3,
            minHeight: 200,
          }}>
            <CircularProgress size={30} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Buscando...
            </Typography>
          </Box>
        ) : filteredConversations.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            p: 3,
            minHeight: 200,
          }}>
            <Box sx={{
              bgcolor: '#f0f2f5',
              borderRadius: 3,
              p: 3,
              textAlign: 'center',
              maxWidth: '90%',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.05)',
            }}>
              <SearchIcon sx={{ 
                fontSize: 48, 
                color: '#c1c7cd',
                mb: 2,
                opacity: 0.5
              }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  color: '#54656f',
                  fontWeight: 500
                }}
              >
                {searchTerm.trim() 
                  ? `No conversations found with "${searchTerm.trim()}"`
                  : 'No conversations available'
                }
              </Typography>
              {searchTerm.trim() && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#8696a0',
                    display: 'block',
                    mt: 1
                  }}
                >
                  Intenta con otro término de búsqueda
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {filteredConversations.map((conversation) => {
              // Check if selected by comparing phone numbers
              const isSelected = sessionId && decodeURIComponent(sessionId) === conversation.participant_phone;
              const phoneInfo = getPhoneCountryInfo(conversation.participant_phone);
              
              return (
                <ListItem 
                  key={conversation.participant_phone} 
                  disablePadding
                  sx={{ 
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <ListItemButton
                    onClick={() => handleConversationClick(conversation)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      bgcolor: isSelected ? '#f0f2f5' : 'transparent',
                      '&:hover': {
                        bgcolor: '#f5f6f6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                      {/* Avatar */}
                      <Avatar
                        sx={{
                          width: 45,
                          height: 45,
                          bgcolor: '#dfe5e7',
                          fontSize: '1.5rem',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        {getInitials(conversation.participant_phone)}
                      </Avatar>

                      {/* Content */}
                      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexGrow: 1 }}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: isSelected ? 600 : 500,
                                fontSize: '0.95rem',
                                color: '#111b21',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flexGrow: 1,
                              }}
                            >
                              {conversation.participant_name || phoneInfo.formattedNumber || conversation.participant_phone}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: conversation.unread_count > 0 ? '#00a884' : '#667781',
                              fontSize: '0.75rem',
                              ml: 2,
                              flexShrink: 0,
                            }}
                          >
                            {formatWhatsappTimestamp(conversation.last_message)}
                          </Typography>
                        </Box>

                        {/* Message Preview with counter badge */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#667781',
                              fontSize: '0.875rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flexGrow: 1,
                            }}
                          >
                            {conversation.message_count > 0 
                              ? `${conversation.message_count} message${conversation.message_count > 1 ? 's' : ''}${conversation.session_count > 1 ? ` · ${conversation.session_count} sessions` : ''}`
                              : 'No messages'}
                          </Typography>
                          {/* Message count badge - always visible */}
                          <Box
                            sx={{
                              bgcolor: conversation.unread_count > 0 ? '#00a884' : '#e0e0e0',
                              color: conversation.unread_count > 0 ? 'white' : '#667781',
                              borderRadius: '12px',
                              px: conversation.message_count >= 100 ? 0.8 : conversation.message_count >= 10 ? 0.7 : 0.6,
                              py: 0.2,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              minWidth: '22px',
                              height: '20px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            {conversation.message_count > 99 ? '99+' : conversation.message_count}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })}
            
            {/* Indicador de carga adicional */}
            {!searchTerm && loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            
            {/* Mensaje cuando se han cargado todas las conversaciones */}
            {!searchTerm && !hasMore && filteredConversations.length > 0 && (
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block', 
                  textAlign: 'center', 
                  p: 2, 
                  color: '#667781' 
                }}
              >
                All conversations loaded ({totalConversations} total)
              </Typography>
            )}
          </List>
        )}
      </Box>

      {/* Footer con el total de conversaciones */}
      <Box 
        sx={{
          borderTop: '1px solid #e0e0e0',
          bgcolor: '#f0f2f5',
          textAlign: 'center',
          flexShrink: 0,
          height: 56,
          boxSizing: 'border-box',
          py: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <FormControl size="small">
          <Select
            value={conversationFilter}
            onChange={(e) => setConversationFilter(e.target.value)}
            sx={{
              fontSize: '0.875rem',
              color: '#667781',
              height: '32px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e0e0e0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#667781',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00a884',
              },
            }}
          >
            <MenuItem value="all" sx={{ fontSize: '0.875rem' }}>All</MenuItem>
            <MenuItem value="answered" sx={{ fontSize: '0.875rem' }}>Just Answered</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ color: '#667781', fontWeight: 500 }}>
          {`${filteredConversations.length.toLocaleString()} of ${totalConversations.toLocaleString()}`}
        </Typography>
      </Box>
    </Box>
  );
};

export default ConversationListPanel;
